import { API_BASE_URL } from '@src/runtimeConfig'

// Protected-media access. The /uploads/:id proxy on backoffice-service now
// requires a valid session, so a plain <img src="/…/uploads/<id>"> (which can't
// send an Authorization header) would 401. Fetch the bytes WITH the Bearer token
// and hand back an object URL instead. Used by <AuthImage>, <FileViewer> and the
// file open/download helpers below (React components use useMediaBlob /
// useObjectUrl from components/useMedia).
//
// The Bearer only ever goes to the API origin (API_BASE_URL). Media paths come
// from the DB and some of those columns are tenant-writable (companies.logo_url
// via PUT /company/:id), so an absolute URL on another host would otherwise hand
// the viewer's access token — cross-tenant for internal users — to that host.
// Such URLs are refused before any request is made; callers show the same
// fallback as for a file that fails to load.

function readToken() {
  try {
    const m = document.cookie.match(/(?:^|;\s*)token-backoffice=([^;]+)/)
    return m ? decodeURIComponent(m[1]) : ''
  } catch {
    return ''
  }
}

// Origin of the API that serves /uploads — the only host allowed to receive the
// Bearer. A relative/empty API_BASE_URL resolves against the page, exactly like
// absoluteMediaUrl below.
function apiOrigin() {
  try {
    return new URL(API_BASE_URL ?? '', window.location.origin).origin
  } catch {
    return ''
  }
}

// Normalize a stored/served media path to an absolute URL on the API origin.
// Stored paths already carry the service homepage prefix (e.g.
// "/backoffice/uploads/<id>") — which API_BASE_URL also contains — so prepend
// only the ORIGIN to avoid a doubled "/backoffice/backoffice". Absolute URLs
// pass through unchanged (check them with isApiMediaUrl before sending a token).
export function absoluteMediaUrl(path) {
  if (!path || typeof path !== 'string') return ''
  if (/^https?:\/\//i.test(path)) return path
  return `${apiOrigin()}${path.startsWith('/') ? path : `/${path}`}`
}

// True only for a media path that resolves onto the API origin, i.e. one that
// may be fetched with the Authorization header.
export function isApiMediaUrl(path) {
  const origin = apiOrigin()
  const url = absoluteMediaUrl(path)
  if (!origin || !url) return false
  try {
    return new URL(url).origin === origin
  } catch {
    return false
  }
}

export class ForeignMediaError extends Error {
  constructor(url) {
    super('media URL is not on the API origin; refusing to send credentials')
    this.name = 'ForeignMediaError'
    this.url = url
  }
}

// Fetch a protected media file with the auth header and return its Blob.
// Throws ForeignMediaError (no request made) when the path isn't on the API
// origin, and a plain Error on a non-2xx response.
export async function fetchMediaBlob(path, signal) {
  const url = absoluteMediaUrl(path)
  if (!url) return null
  if (!isApiMediaUrl(url)) throw new ForeignMediaError(url)
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${readToken()}` },
    signal,
  })
  if (!res.ok) throw new Error(`media request failed (${res.status})`)
  return res.blob()
}

// Fetch a protected media file with the auth header and return a blob object URL.
// Caller owns the returned URL and must URL.revokeObjectURL() it when done.
// For an <img> only: the blob keeps the type the proxy sent. Anything shown as
// a document (a new tab, an <iframe>) goes through safeTypedBlob first.
export async function fetchMediaObjectUrl(path, signal) {
  const blob = await fetchMediaBlob(path, signal)
  return blob ? URL.createObjectURL(blob) : null
}

// Types a protected file may be rendered as on this app's own origin. A blob:
// URL has the origin of the page that made it, so a new tab or an <iframe> of
// one runs with the app's cookies (the session token among them). The type a
// fetched blob carries is the uploads proxy's, which takes it from the stored
// id's extension, i.e. from the file name the uploader chose: an "x.svg" sent
// as a document is served as image/svg+xml and, opened as a document, would
// run its script here (the proxy's CSP sandbox does not travel with the blob).
// None of these types runs script. An <img> may show any image type (an SVG
// runs no script there), so AuthImage needs none of this.
const INLINE_SAFE_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'text/plain',
])

const baseType = (type) => String(type || '').split(';')[0].trim().toLowerCase()

/** Whether a file of this MIME type may be rendered on this origin (a new tab, an <iframe>). */
export function isInlineSafeType(type) {
  return INLINE_SAFE_TYPES.has(baseType(type))
}

/**
 * `blob` typed so that rendering it on this origin runs no script: as `type`
 * when given and safe (what the caller knows the file is, e.g. application/pdf
 * for an HR document), else as its own type when that is safe, else as
 * application/octet-stream, which a browser downloads instead of rendering.
 */
export function safeTypedBlob(blob, type) {
  if (!blob) return blob
  let safe = 'application/octet-stream'
  if (type && isInlineSafeType(type)) safe = baseType(type)
  else if (isInlineSafeType(blob.type)) safe = baseType(blob.type)
  return blob.type === safe ? blob : blob.slice(0, blob.size, safe)
}

// The last path segment of a media URL (the stored id): a download's name
// when the caller knows no better one.
function mediaFileName(path) {
  const segment = String(path || '')
    .split(/[?#]/)[0]
    .split('/')
    .pop()
  return segment || 'file'
}

/**
 * Show a Blob the app holds in a new tab, typed by safeTypedBlob(blob, type);
 * a file whose type is not safe to render here is saved as `fileName` instead.
 * The object URL is revoked after a delay once the new tab has loaded it.
 */
export function openBlob(blob, { type, fileName } = {}) {
  if (!blob) return
  const typed = safeTypedBlob(blob, type)
  if (!isInlineSafeType(typed.type)) {
    saveBlob(typed, fileName)
    return
  }
  const objectUrl = URL.createObjectURL(typed)
  window.open(objectUrl, '_blank', 'noopener')
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
}

// Open a protected file (PDF / document / letter) in a new tab via a blob URL,
// so the Authorization header is sent instead of leaking a token in the URL.
// `type`: what the caller knows the file is (application/pdf for an HR
// document, the row's allow-listed type for a photo); `fileName`: its name
// when it has to be downloaded instead (openBlob).
export async function openMedia(path, { type, fileName } = {}) {
  const blob = await fetchMediaBlob(path)
  openBlob(blob, { type, fileName: fileName || mediaFileName(path) })
}

// Percent-decode an RFC 5987 value (`charset'lang'value`); null when malformed.
function decodeExtValue(ext) {
  const m = /^([^']*)'[^']*'(.*)$/.exec(ext)
  if (!m) return null
  const charset = m[1].toLowerCase()
  try {
    if (charset === 'utf-8') return decodeURIComponent(m[2])
    if (charset === 'iso-8859-1') {
      return m[2].replace(/%([0-9a-f]{2})/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
    }
  } catch {
    return null
  }
  return null
}

/**
 * The file name a download's Content-Disposition header names (RFC 6266):
 * `filename*` (RFC 5987, e.g. UTF-8''n%C3%A9.xlsx) first, then `filename`
 * (quoted or bare). Only the last path segment is kept. null when the header
 * is absent or names nothing (the caller picks a fallback).
 */
export function fileNameFromContentDisposition(header) {
  if (!header || typeof header !== 'string') return null
  const baseName = (name) => (name ? name.split(/[\\/]/).pop().trim() || null : null)

  const star = /(?:^|;)\s*filename\*\s*=\s*("[^"]*"|[^;]*)/i.exec(header)
  const fromStar = star ? baseName(decodeExtValue(star[1].trim().replace(/^"(.*)"$/, '$1'))) : null
  if (fromStar) return fromStar

  const plain = /(?:^|;)\s*filename\s*=\s*(?:"((?:[^"\\]|\\.)*)"|([^;]*))/i.exec(header)
  if (!plain) return null
  return baseName(plain[1] !== undefined ? plain[1].replace(/\\(.)/g, '$1') : plain[2])
}

// Save a Blob the app already holds (a protected media file, an API file
// download) under `filename`, via a short-lived object URL.
export function saveBlob(blob, filename) {
  if (!blob) return
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = filename || 'download'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000)
}
